// ---------------------------------------------------------------------------
// Vendor KPI system — FIFA Ultimate Team inspired (6 categories, 14 KPIs, 0-99)
// ---------------------------------------------------------------------------

// --- Category IDs ---
// COM (Comunicación / acoustic KPIs) removed from MVP — requires audio pipeline.
// Will be reintroduced when audio analysis is available.
export type VendorKpiCategoryId = 'PRG' | 'PIT' | 'EMP' | 'NEG' | 'CIE';

// --- Detail KPI ---
export interface VendorKpiDetail {
  id: string;
  label: string;
  score: number; // 0–99
  displayValue?: string;
  description: string;
}

// --- Category ---
export interface VendorKpiCategory {
  id: VendorKpiCategoryId;
  name: string;
  shortName: string;
  kpis: VendorKpiDetail[];
  score: number; // 0–99 (avg of kpis)
}

// --- Full profile ---
export interface VendorProfile {
  globalScore: number; // 0–99
  categories: VendorKpiCategory[];
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

export const VENDOR_CATEGORIES: Record<VendorKpiCategoryId, { nameKey: string; shortName: string }> = {
  PRG: { nameKey: 'vendor.categories.PRG', shortName: 'PRG' },
  PIT: { nameKey: 'vendor.categories.PIT', shortName: 'PIT' },
  EMP: { nameKey: 'vendor.categories.EMP', shortName: 'EMP' },
  NEG: { nameKey: 'vendor.categories.NEG', shortName: 'NEG' },
  CIE: { nameKey: 'vendor.categories.CIE', shortName: 'CIE' },
};

/** 11 KPIs mapped to 5 categories (COM removed from MVP — needs audio pipeline) */
export const KPI_TEMPLATE: { id: string; labelKey: string; category: VendorKpiCategoryId; descriptionKey: string }[] = [
  // PRG — Preguntas y escucha (3)
  { id: 'prg_preguntas', labelKey: 'vendor.kpis.prg_preguntas.label', category: 'PRG', descriptionKey: 'vendor.kpis.prg_preguntas.description' },
  { id: 'prg_escucha', labelKey: 'vendor.kpis.prg_escucha.label', category: 'PRG', descriptionKey: 'vendor.kpis.prg_escucha.description' },
  { id: 'prg_confirmaciones', labelKey: 'vendor.kpis.prg_confirmaciones.label', category: 'PRG', descriptionKey: 'vendor.kpis.prg_confirmaciones.description' },
  // PIT — Pitch y argumentación (3)
  { id: 'pit_duracion', labelKey: 'vendor.kpis.pit_duracion.label', category: 'PIT', descriptionKey: 'vendor.kpis.pit_duracion.description' },
  { id: 'pit_beneficios', labelKey: 'vendor.kpis.pit_beneficios.label', category: 'PIT', descriptionKey: 'vendor.kpis.pit_beneficios.description' },
  { id: 'pit_lexico', labelKey: 'vendor.kpis.pit_lexico.label', category: 'PIT', descriptionKey: 'vendor.kpis.pit_lexico.description' },
  // EMP — Empatía y rapport (2)
  { id: 'emp_engagement', labelKey: 'vendor.kpis.emp_engagement.label', category: 'EMP', descriptionKey: 'vendor.kpis.emp_engagement.description' },
  { id: 'emp_reformulaciones', labelKey: 'vendor.kpis.emp_reformulaciones.label', category: 'EMP', descriptionKey: 'vendor.kpis.emp_reformulaciones.description' },
  // NEG — Negociación (1)
  { id: 'neg_precio', labelKey: 'vendor.kpis.neg_precio.label', category: 'NEG', descriptionKey: 'vendor.kpis.neg_precio.description' },
  // CIE — Cierre (2)
  { id: 'cie_nextstep', labelKey: 'vendor.kpis.cie_nextstep.label', category: 'CIE', descriptionKey: 'vendor.kpis.cie_nextstep.description' },
  { id: 'cie_primervalor', labelKey: 'vendor.kpis.cie_primervalor.label', category: 'CIE', descriptionKey: 'vendor.kpis.cie_primervalor.description' },
];

// ---------------------------------------------------------------------------
// Score tier helpers
// ---------------------------------------------------------------------------

export type ScoreTier = 'excellent' | 'good' | 'medium' | 'low';

export function getScoreTier(score: number): ScoreTier {
  if (score >= 85) return 'excellent';
  if (score >= 70) return 'good';
  if (score >= 50) return 'medium';
  return 'low';
}

export const SCORE_COLORS: Record<ScoreTier, string> = {
  excellent: 'var(--primary)',
  good: 'var(--color-blue-500)',
  medium: 'var(--color-gray-400)',
  low: 'var(--color-red-500)',
};

export function getScoreColor(score: number): string {
  return SCORE_COLORS[getScoreTier(score)];
}

// ---------------------------------------------------------------------------
// Card tier (border style)
// ---------------------------------------------------------------------------

export type CardTier = 'elite' | 'pro' | 'standard';

export function getCardTier(globalScore: number): CardTier {
  if (globalScore >= 85) return 'elite';
  if (globalScore >= 65) return 'pro';
  return 'standard';
}
